// Config and hooks shared across the named `@github-ui/sanitize` presets.
//
// The image hook guards on browser globals, so the server (linkedom) path is a
// safe no-op for it. The link hook runs isomorphically: linkedom exposes `target`,
// so it hardens links on both the client and the server.

import type {Config} from '../types'

// ── Base config shared across the presets ─────────────────────────────────────
// The default-deny posture every preset inherits. `ALLOW_DATA_ATTR: false` denies
// all `data-*` attributes; presets re-permit specific markers via their own
// `ADD_ATTR` / `ADD_URI_SAFE_ATTR` allowlists.
export const SHARED_CONFIG: Readonly<Config> = {
  FORBID_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
}

// ── Link hardening hook (always on) ───────────────────────────────────────────

// Ensure all sanitized links open in a new window.
// https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
export const sanitizeNewWindowLinks = (node: Element) => {
  // set all elements owning target to target=_blank
  if ('target' in node || (typeof HTMLAnchorElement !== 'undefined' && node instanceof HTMLAnchorElement)) {
    node.setAttribute('target', '_blank')
    // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
    node.setAttribute('rel', 'noopener noreferrer')
  }
  // set non-HTML/MathML links to xlink:show=new
  // eslint-disable-next-line github/get-attribute
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    // eslint-disable-next-line github/get-attribute
    node.setAttribute('xlink:show', 'new')
  }
}

// ── Image-domain hardening (#2038) ────────────────────────────────────────────

/** Domains controlled by GitHub and considered safe to load images from. */
const GITHUB_CONTROLLED_DOMAINS = new Set([
  'github.com',
  'githubusercontent.com',
  'githubassets.com',
  'github.localhost',
  'ghe.com',
  'ghe.localhost',
])

/** Subdomains allowed when subdomain isolation is enabled in GHES. */
const GHES_SUBDOMAINS = new Set(['media', 'uploads', 'assets', 'avatars'])

/**
 * Whether an image URL resolves to a GitHub-controlled or same-origin host.
 * Fail-closed: any parse failure returns `false` so the caller drops the element.
 */
const isGitHubControlledImageUrl = (rawUrl: string): boolean => {
  try {
    // Resolve against `window.location.origin` (not `document.baseURI`): it is the
    // repo-standard base for `new URL()` and, for a sanitizer, is more robust — a
    // malicious injected `<base>` tag cannot redirect resolution.
    const url = new URL(rawUrl, window.location.origin)
    // Allow relative / same-origin URLs first, regardless of hostname shape: a
    // GHES appliance may run on a single-label host (e.g. `https://github`),
    // where the domain-shape check below would otherwise drop same-origin images.
    if (url.origin === window.location.origin) return true
    const urlParts = url.hostname.split('.')
    if (urlParts.length >= 2) {
      const baseDomain = `${urlParts.at(-2)}.${urlParts.at(-1)}`
      // Allow github-controlled domains explicitly, and nothing else.
      if (GITHUB_CONTROLLED_DOMAINS.has(baseDomain)) return true
      // Allow the current GitHub URL's isolation subdomains (GHES).
      const githubUrlScript = document.getElementById('github-url')
      if (githubUrlScript?.textContent) {
        // `#github-url` is a JSON island: its textContent is a JSON-encoded
        // (quoted) absolute URL, so it must be parsed before use. Feeding the
        // raw quoted text to `new URL(text, origin)` would treat it as a
        // relative path and derive the isolation subdomains from the page host
        // instead of the canonical GitHub host. Any parse failure falls through
        // to the outer catch and drops the element (fail-closed).
        const parsed: unknown = JSON.parse(githubUrlScript.textContent)
        if (typeof parsed === 'string' && parsed) {
          const githubUrl = new URL(parsed, window.location.origin)
          const validSubdomains = Array.from(GHES_SUBDOMAINS.values()).map(
            subdomain => `${subdomain}.${githubUrl.hostname}`,
          )
          if (validSubdomains.includes(url.hostname)) return true
        }
      }
    }
    return false
  } catch {
    return false
  }
}

/**
 * Extract the candidate URLs from a `srcset` value. Candidates are comma-separated
 * `url [descriptor]` entries; we take the leading URL token of each. Fail-closed by
 * design: a value we cannot cleanly tokenize yields a candidate the domain check
 * will reject, so the element is dropped rather than trusted.
 */
const parseSrcsetCandidateUrls = (srcset: string): string[] =>
  srcset
    .split(',')
    .map(candidate => candidate.trim().split(/[ \t\n\f\r]+/)[0] ?? '')
    .filter(Boolean)

/**
 * Restrict images to GitHub-controlled domains by removing disallowed image nodes.
 * The intent is to allow our own images (e.g. `:octocat:`) and same-origin uploads,
 * not arbitrary user-controlled hosts. This is not strictly a security boundary —
 * the content is still DOMPurify-filtered — but keeps rendered-markdown image hosts
 * explicit. Browser-guarded so the server (linkedom) path is a safe no-op. The
 * presets deliberately omit memex's `skipImageValidation` toggle: image validation
 * is unconditional.
 *
 * Both `src` and `srcset` are validated, on `<img>` and on `<picture>`'s `<source>`
 * elements: DOMPurify permits `srcset` by default, so validating only `src` would
 * let `<img srcset="https://off-domain/x 1x">` (or a `<source srcset>`) load an
 * off-domain candidate and bypass the restriction. Any off-domain (or unparseable)
 * candidate drops the whole element, matching the off-domain `src` behavior.
 */
export const sanitizeNonGitHubImageLinks = (node: Node) => {
  if (typeof document === 'undefined') return
  if (!(node instanceof Element)) return

  // `<g-emoji fallback-src>` is copied into a freshly created `img.src` by
  // @github/g-emoji-element's connectedCallback when native emoji support is
  // unavailable, so an off-domain fallback-src still issues an off-domain image
  // request — the same vector the `<img>` path closes. Neutralize by dropping just
  // the attribute (the emoji still renders natively/as text) rather than the element.
  if (node.tagName === 'G-EMOJI') {
    const fallbackSrc = node.getAttribute('fallback-src')
    if (fallbackSrc && !isGitHubControlledImageUrl(fallbackSrc)) {
      node.removeAttribute('fallback-src')
    }
    return
  }

  // Only images are in scope: `<img>` (carries `src`/`srcset`), and `<source>` under
  // `<picture>` (carries `srcset` for responsive images). A `<source>` under
  // `<video>`/`<audio>` is media, not an image, so it is left untouched — restricting
  // it would strip legit off-domain media beyond this hook's #2038 image-source scope.
  if (node.tagName === 'SOURCE') {
    if (node.parentElement?.tagName !== 'PICTURE') return
  } else if (node.tagName !== 'IMG') {
    return
  }

  const imageSrc = node.getAttribute('src')
  if (imageSrc && !isGitHubControlledImageUrl(imageSrc)) {
    node.parentNode?.removeChild(node)
    return
  }

  const srcset = node.getAttribute('srcset')
  if (srcset) {
    const candidates = parseSrcsetCandidateUrls(srcset)
    if (candidates.length === 0 || candidates.some(candidate => !isGitHubControlledImageUrl(candidate))) {
      node.parentNode?.removeChild(node)
    }
  }
}
